const PROD_WIDGET_HOST = "widget2.chio-chio.ru";
const PROD_MOBILE_HOST = "mobile2.chio-chio.ru";
function trimString(value) {
    return typeof value === "string" ? value.trim() : "";
}
function normalizeFlushResult(value) {
    if (!value || typeof value !== "object") {
        return {};
    }
    return {
        refreshToken: trimString(value.refreshToken),
        fingerprint: trimString(value.fingerprint),
    };
}
function getGlobalSessionStorage() {
    try {
        return typeof window !== "undefined" ? window.sessionStorage : undefined;
    }
    catch {
        return undefined;
    }
}
function readStoredRefreshToken(storage) {
    try {
        return trimString((storage || getGlobalSessionStorage())?.getItem("refresh_token"));
    }
    catch {
        return "";
    }
}
function readNativeSessionBootstrap() {
    try {
        if (typeof window === "undefined") {
            return undefined;
        }
        const value = window.__CC_NATIVE_SESSION_BOOTSTRAP__;
        return value && typeof value === "object" ? value : undefined;
    }
    catch {
        return undefined;
    }
}
function hasNativeBootstrappedSession(value) {
    const bootstrap = value || readNativeSessionBootstrap();
    return Boolean(bootstrap?.hasRefreshToken && bootstrap?.hasFingerprint);
}
function postNativeLoginExport(args) {
    if (!args.refreshToken) {
        return;
    }
    const payload = {
        access_token: args.accessToken || "",
        refresh_token: args.refreshToken || "",
        fp: args.fingerprint,
        src: "id_login_success",
    };
    if (args.supportsNativeLoginRedirect) {
        payload.return_to = args.target;
    }
    args.postToNative("refresh_export_inline", payload, { includeSessionEpoch: false });
}
export function getConfiguredWidgetTarget(appFallbackHome) {
    if (!appFallbackHome)
        return "";
    try {
        return new URL(appFallbackHome).toString();
    }
    catch {
        return "";
    }
}
export function getDefaultWidgetOrigin(args) {
    const configuredTarget = getConfiguredWidgetTarget(args.appFallbackHome);
    if (configuredTarget) {
        try {
            return new URL(configuredTarget).origin;
        }
        catch {
            // ignore
        }
    }
    const protocol = args.protocol === "http:" || args.protocol === "https:" ? args.protocol : "https:";
    const hostname = trimString(args.hostname);
    if (hostname.endsWith(".devchio.ru")) {
        return `${protocol}//widget.devchio.ru`;
    }
    return `${protocol}//widget2.chio-chio.ru`;
}
export function getDefaultWidgetTarget(args) {
    const fallbackSource = getConfiguredWidgetTarget(args.appFallbackHome) ||
        getDefaultWidgetOrigin({
            appFallbackHome: args.appFallbackHome,
            protocol: args.protocol,
            hostname: args.hostname,
        });
    try {
        const url = new URL(fallbackSource, args.origin || "https://widget2.chio-chio.ru");
        if (url.pathname === "/" || url.pathname === "") {
            url.pathname = "/home";
        }
        return url.toString();
    }
    catch {
        const normalized = fallbackSource.replace(/\/$/, "");
        return `${normalized}/home`;
    }
}
export function getRedirectTarget(args) {
    try {
        const url = new URL(args.href);
        const redir = url.searchParams.get("returnTo");
        if (redir && /^https:\/\/[^\s]+$/.test(redir)) {
            return redir;
        }
    }
    catch {
        // ignore
    }
    return getDefaultWidgetTarget({
        appFallbackHome: args.appFallbackHome,
        origin: args.origin,
        protocol: args.protocol,
        hostname: args.hostname,
    });
}
export function normalizeMobileReturnTarget(target, isNativeWebView) {
    if (!isNativeWebView) {
        return target;
    }
    try {
        const url = new URL(target);
        if (url.protocol === "https:" && url.hostname === PROD_WIDGET_HOST) {
            url.hostname = PROD_MOBILE_HOST;
            return url.toString();
        }
    }
    catch {
        // keep the original target
    }
    return target;
}
export async function cookieFlushPing(args) {
    let result = {};
    const fetcher = args.fetchImpl || fetch;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    await sleep(args.firstDelayMs ?? 150);
    try {
        if (args.isInWebView) {
            const response = await fetcher(`${args.apiBase}/api/refresh/export`, {
                method: "GET",
                credentials: "include",
                headers: {
                    "X-CSRF-Token": args.fingerprint,
                    "X-Client": "mobile",
                    "Cache-Control": "no-store",
                },
            }).catch(() => { });
            if (response && "ok" in response && response.ok && typeof response.json === "function") {
                const data = await response.json().catch(() => null);
                result = {
                    refreshToken: trimString(data?.refresh_token),
                    fingerprint: trimString(data?.fp),
                };
            }
            if (!result.refreshToken && hasNativeBootstrappedSession(args.nativeSessionBootstrap)) {
                const refreshToken = readStoredRefreshToken(args.sessionStorage);
                if (refreshToken) {
                    const refreshResponse = await fetcher(`${args.apiBase}/api/refresh`, {
                        method: "POST",
                        credentials: "include",
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRF-Token": args.fingerprint,
                            "X-Refresh-Token": refreshToken,
                            "X-Client": "mobile",
                            "Cache-Control": "no-store",
                        },
                        body: JSON.stringify({
                            refresh_token: refreshToken,
                        }),
                    }).catch(() => { });
                    if (refreshResponse &&
                        "ok" in refreshResponse &&
                        refreshResponse.ok &&
                        typeof refreshResponse.json === "function") {
                        const data = await refreshResponse.json().catch(() => null);
                        result = {
                            refreshToken: trimString(data?.refresh_token) || refreshToken,
                            fingerprint: args.fingerprint,
                        };
                    }
                }
            }
        }
    }
    catch {
        // ignore
    }
    await sleep(args.secondDelayMs ?? 200);
    return result;
}
export async function finalizeLoginFlow(args) {
    const target = normalizeMobileReturnTarget(getRedirectTarget({
        href: args.location.href,
        appFallbackHome: args.appFallbackHome,
        origin: args.location.origin,
        protocol: args.location.protocol,
        hostname: args.location.hostname,
    }), args.isNativeWebView);
    try {
        if (args.accessToken)
            args.sessionStorage.setItem("access_token", args.accessToken);
    }
    catch {
        // ignore
    }
    try {
        if (args.refreshToken)
            args.sessionStorage.setItem("refresh_token", args.refreshToken);
    }
    catch {
        // ignore
    }
    postNativeLoginExport({
        accessToken: args.accessToken,
        refreshToken: args.refreshToken,
        fingerprint: args.fingerprint,
        target,
        supportsNativeLoginRedirect: args.supportsNativeLoginRedirect,
        postToNative: args.postToNative,
    });
    const exported = normalizeFlushResult(await args.flushCookies(args.fingerprint));
    const finalRefreshToken = args.refreshToken || exported.refreshToken || "";
    const finalFingerprint = exported.fingerprint || args.fingerprint;
    try {
        if (!args.refreshToken && exported.refreshToken) {
            args.sessionStorage.setItem("refresh_token", exported.refreshToken);
        }
    }
    catch {
        // ignore
    }
    postNativeLoginExport({
        accessToken: args.accessToken,
        refreshToken: finalRefreshToken,
        fingerprint: finalFingerprint,
        target,
        supportsNativeLoginRedirect: args.supportsNativeLoginRedirect,
        postToNative: args.postToNative,
    });
    try {
        args.location.replace?.(target);
    }
    catch {
        args.assignHref(target);
    }
    return target;
}
